import {
  datesPerPass,
  type PassTypeLike,
} from "@common/purchase/pass-quantity-selector-logic";
import {
  formatPassDateRange,
  type SeriesCartPassDate,
} from "@common/purchase/series-cart";

// Grouping for the "Personalize Tickets" panel. A multi-day pass is stored as one
// Ticket per covered date, so personalizing per ticket asked for the same name once per day.
//
// Parameters are typed structurally so the Coalesce models and minimal test fixtures both
// satisfy them, matching series-cart.ts and pass-quantity-selector-logic.ts.

export interface PersonalizationTicket {
  ticketId?: number | null;
  passTypeId?: number | null;
  eventDateId?: number | null;
  assignedFirstName?: string | null;
  assignedLastName?: string | null;
  seatNumber?: string | null;
  seatSectionName?: string | null;
  seatLabel?: string | null;
  displayName?: string | null;
  isScanned?: boolean | null;
}

export interface PersonalizationPassType extends PassTypeLike {
  name?: string | null;
}

export type PersonalizationGroupKind =
  | "multiDayPass"
  | "singleDayPass"
  | "ticket";

export interface PersonalizationGroup {
  /** Must stay stable across reloads: the in-progress edit state hangs off it. */
  key: string;
  kind: PersonalizationGroupKind;
  /** Every ticket the group's name is written to — 1 for a ticket, N for a multi-day pass. */
  ticketIds: number[];
  /** Pass name, or "Ticket N" for a non-pass ticket. */
  title: string;
  /** "Pass 1 of 2" / "Ticket 1 of 2"; empty when the group stands alone. */
  ordinalLabel: string;
  dateLabel: string;
  /** Drives the "applies to all N days" note. */
  dayCount: number;
  /** Empty when the ticket has no seat, which is what hides the chip. */
  seatLabel: string;
  scannedCount: number;
  assignedFirstName: string;
  assignedLastName: string;
  /** Legacy rows inside one group that disagree — the first non-empty name wins. */
  hasDivergentNames: boolean;
}

function isNonEmpty(value: string | null | undefined): boolean {
  return !!value && value.trim().length > 0;
}

function nameKey(ticket: PersonalizationTicket): string {
  return `${ticket.assignedFirstName?.trim() ?? ""}|${ticket.assignedLastName?.trim() ?? ""}`;
}

/** Unchanged from the previous format. "" stands in for the old `v-if="ticket.seatNumber"` guard. */
export function formatSeatLabel(ticket: PersonalizationTicket): string {
  if (!ticket.seatNumber) return "";

  const prefix = ticket.displayName;

  // A resolved seat-map label ("6 A1", or a GA section title) is what the patron sees on their
  // own seat, so it beats the raw section + number.
  if (ticket.seatLabel) {
    return `${prefix} - ${ticket.seatLabel}`;
  }

  const trimmedSeatNumber = ticket.seatNumber.endsWith("-")
    ? ticket.seatNumber.slice(0, -1)
    : ticket.seatNumber;

  if (ticket.seatSectionName) {
    return `${prefix} - ${ticket.seatSectionName}: ${trimmedSeatNumber}`;
  }
  return `${prefix} - ${trimmedSeatNumber}`;
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

function groupNameState(tickets: PersonalizationTicket[]) {
  const named = tickets.filter(
    (t) => isNonEmpty(t.assignedFirstName) || isNonEmpty(t.assignedLastName),
  );
  const seed = named[0];
  return {
    assignedFirstName: seed?.assignedFirstName?.trim() ?? "",
    assignedLastName: seed?.assignedLastName?.trim() ?? "",
    hasDivergentNames: new Set(named.map(nameKey)).size > 1,
    scannedCount: tickets.filter((t) => t.isScanned === true).length,
    ticketIds: tickets.map((t) => t.ticketId!),
  };
}

/**
 * One group per pass sold for multi-date passes, one group per ticket for everything else.
 *
 * Chunking is positional — the pass's tickets sorted by id, split into runs of `datesPerPass` —
 * to match how the backend numbers a ticket within its pass group (PricingService derives
 * PassGroupIndex over `Tickets.OrderBy(t => t.Id)` to split per-pass price and fees), so the UI
 * and the money agree on what "one pass" means. It holds over a purchase's life because refunds
 * and exchanges *flag* ticket rows rather than deleting them, and cart-stage quantity reduction
 * removes whole passes. Only a purchase created by a per-date exchange yields a short run; that
 * run becomes its own group with its real dayCount.
 *
 * Keyed off dates covered rather than PassCategory alone, so a pass mis-configured as MultiDay
 * with a single date still behaves like a single-day pass.
 */
export function buildPersonalizationGroups(
  tickets: PersonalizationTicket[],
  passTypes: PersonalizationPassType[],
): PersonalizationGroup[] {
  const ordered = [...(tickets ?? [])]
    .filter((t) => t.ticketId != null)
    .sort((a, b) => a.ticketId! - b.ticketId!);

  const passTypeById = new Map<number, PersonalizationPassType>();
  for (const pt of passTypes ?? []) {
    if (pt.id != null) passTypeById.set(pt.id, pt);
  }

  // An unresolvable pass type falls back to per-ticket rather than dropping the ticket.
  const ticketsByPassType = new Map<number, PersonalizationTicket[]>();
  const looseTickets: PersonalizationTicket[] = [];
  for (const t of ordered) {
    const passType =
      t.passTypeId == null ? undefined : passTypeById.get(t.passTypeId);
    if (!passType) {
      looseTickets.push(t);
      continue;
    }
    const existing = ticketsByPassType.get(t.passTypeId!);
    if (existing) existing.push(t);
    else ticketsByPassType.set(t.passTypeId!, [t]);
  }

  const multiDayGroups: PersonalizationGroup[] = [];
  const singleDayGroups: PersonalizationGroup[] = [];

  const orderedPassTypeIds = [...ticketsByPassType.keys()].sort((a, b) => {
    const ptA = passTypeById.get(a)!;
    const ptB = passTypeById.get(b)!;
    return (ptA.order ?? 0) - (ptB.order ?? 0) || a - b;
  });

  for (const passTypeId of orderedPassTypeIds) {
    const passType = passTypeById.get(passTypeId)!;
    const passTickets = ticketsByPassType.get(passTypeId)!;
    const perPass = datesPerPass(passType);
    const title = passType.name ?? "Pass";

    // Label a group by the dates it actually covers — matters for the short run an exchange leaves.
    const passDateByEventDateId = new Map<number, SeriesCartPassDate>();
    for (const ptd of passType.passTypeDates ?? []) {
      if (ptd.eventDate?.id != null) {
        passDateByEventDateId.set(ptd.eventDate.id, ptd);
      }
    }
    const labelForTickets = (groupTickets: PersonalizationTicket[]) => {
      const dates = groupTickets
        .map((t) =>
          t.eventDateId == null
            ? undefined
            : passDateByEventDateId.get(t.eventDateId),
        )
        .filter((d): d is SeriesCartPassDate => !!d);
      return formatPassDateRange(
        dates.length > 0 ? dates : (passType.passTypeDates ?? []),
      );
    };

    if (perPass <= 1) {
      passTickets.forEach((ticket, index) => {
        singleDayGroups.push({
          key: `pass:${passTypeId}:${ticket.ticketId}`,
          kind: "singleDayPass",
          title,
          ordinalLabel:
            passTickets.length > 1
              ? `Ticket ${index + 1} of ${passTickets.length}`
              : "",
          dateLabel: labelForTickets([ticket]),
          dayCount: 1,
          seatLabel: formatSeatLabel(ticket),
          ...groupNameState([ticket]),
        });
      });
      continue;
    }

    const runs = chunk(passTickets, perPass);
    runs.forEach((run, index) => {
      multiDayGroups.push({
        key: `pass:${passTypeId}:${run[0].ticketId}`,
        kind: "multiDayPass",
        title,
        ordinalLabel:
          runs.length > 1 ? `Pass ${index + 1} of ${runs.length}` : "",
        dateLabel: labelForTickets(run),
        dayCount: run.length,
        seatLabel: "",
        ...groupNameState(run),
      });
    });
  }

  // Non-pass tickets keep their previous presentation exactly — the no-regression path.
  const looseGroups: PersonalizationGroup[] = looseTickets.map(
    (ticket, index) => ({
      key: `ticket:${ticket.ticketId}`,
      kind: "ticket",
      title: `Ticket ${index + 1}`,
      ordinalLabel: "",
      dateLabel: "",
      dayCount: 1,
      seatLabel: formatSeatLabel(ticket),
      ...groupNameState([ticket]),
    }),
  );

  // Multi-day first so the optional section headers render as two contiguous blocks.
  return [...multiDayGroups, ...singleDayGroups, ...looseGroups];
}

/** Section headers are only meaningful when the cart holds both kinds of pass. */
export function shouldShowSectionHeaders(
  groups: PersonalizationGroup[],
): boolean {
  return (
    groups.some((g) => g.kind === "multiDayPass") &&
    groups.some((g) => g.kind === "singleDayPass")
  );
}

/** The save endpoint takes a flat per-ticket list, so a group's name fans out to all its tickets. */
export function expandGroupNames(
  groups: PersonalizationGroup[],
  nameFor: (group: PersonalizationGroup) => {
    firstName: string;
    lastName: string;
  },
): {
  ticketId: number;
  assignedFirstName: string | null;
  assignedLastName: string | null;
}[] {
  return groups.flatMap((group) => {
    const { firstName, lastName } = nameFor(group);
    return group.ticketIds.map((ticketId) => ({
      ticketId,
      assignedFirstName: firstName.trim() || null,
      assignedLastName: lastName.trim() || null,
    }));
  });
}
